// utils/formatSubtitle.ts
export type SubtitleOptions = {
  locale?: string; // e.g., "pt-PT" | "en-US"
  timeZone?: string; // e.g., "Europe/Lisbon"
  hourCycle?: "h12" | "h23"; // force 12h/24h for time ranges
};

const DEFAULT_LOCALE = "pt-PT";
const DEFAULT_TZ = "Europe/Lisbon";

const normalize = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/** PT + EN month names → month index (0–11) */
const MONTHS: Record<string, number> = {
  january: 0,
  janeiro: 0,
  february: 1,
  fevereiro: 1,
  march: 2,
  marco: 2,
  março: 2,
  april: 3,
  abril: 3,
  may: 4,
  maio: 4,
  june: 5,
  junho: 5,
  july: 6,
  julho: 6,
  august: 7,
  agosto: 7,
  september: 8,
  setembro: 8,
  october: 9,
  outubro: 9,
  november: 10,
  novembro: 10,
  december: 11,
  dezembro: 11,
};

/**
 * Formats a subtitle string, auto-detecting one of:
 * - Time range: "0:00 - 16:00" (supports -, –, —)
 * - Date range: "de 2025-10-13 a 2025-10-19" (or "2025-10-13 - 2025-10-19")
 * - Month+Year: "setembro, 2025" or "setembro 2025"
 *
 * Returns the input unchanged if no pattern matches.
 */
export function formatSubtitle(
  s: string,
  {
    locale = DEFAULT_LOCALE,
    timeZone = DEFAULT_TZ,
    hourCycle,
  }: SubtitleOptions = {}
): string {
  const input = s.trim();
  const common: Intl.DateTimeFormatOptions = {
    timeZone,
    ...(hourCycle ? { hourCycle } : {}),
  };
  const hasRange =
    typeof (Intl.DateTimeFormat.prototype as any).formatRange === "function";

  /** 1) Time range: "0:00 - 16:00" (supports -, –, —) */
  if (/^\s*\d{1,2}:\d{2}\s*[-–—]\s*\d{1,2}:\d{2}\s*$/u.test(input)) {
    const [a, b] = input.split(/[-–—]/).map((x) => x.trim());
    const today = new Date();
    const [h1, m1] = a.split(":").map(Number);
    const [h2, m2] = b.split(":").map(Number);
    const start = new Date(today);
    start.setHours(h1, m1 || 0, 0, 0);
    const end = new Date(today);
    end.setHours(h2, m2 || 0, 0, 0);

    const tfmt = new Intl.DateTimeFormat(locale, {
      ...common,
      hour: "2-digit",
      minute: "2-digit",
    });
    return hasRange
      ? (tfmt as any).formatRange(start, end)
      : `${tfmt.format(start)} – ${tfmt.format(end)}`;
  }

  /** 2) Date range (ISO-like): "de 2025-10-13 a 2025-10-19" (prefix optional) OR "2025-10-13 - 2025-10-19" */
  const normalized = normalize(input);
  if (/\d{4}-\d{2}-\d{2}.*[-–—a]\s*\d{4}-\d{2}-\d{2}/u.test(normalized)) {
    const match = input.match(
      /(\d{4})-(\d{2})-(\d{2}).*[-–—a]\s*(\d{4})-(\d{2})-(\d{2})/u
    );
    if (match) {
      const start = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
      const end = new Date(
        Number(match[4]),
        Number(match[5]) - 1,
        Number(match[6])
      );
      const dfmt = new Intl.DateTimeFormat(locale, {
        ...common,
        year: "2-digit",
        month: "2-digit",
        day: "numeric",
      });
      return hasRange
        ? (dfmt as any).formatRange(start, end)
        : `${dfmt.format(start)} – ${dfmt.format(end)}`;
    }
  }

  /**  3) Month + year: "setembro, 2025" OR "setembro 2025" */
  if (/^[^\d]+,\s*\d{4}$|^[^\d]+\s+\d{4}$/u.test(input)) {
    const parts = input.includes(",")
      ? input.split(",").map((p) => p.trim())
      : input.split(/\s+/);

    if (parts.length >= 2) {
      const monthName = parts[0];
      const year = Number(parts[1]);
      const monthIdx = MONTHS[normalize(monthName)];
      if (monthIdx != null && Number.isFinite(year)) {
        const date = new Date(year, monthIdx, 1);
        return new Intl.DateTimeFormat(locale, {
          ...common,
          year: "numeric",
          month: "long",
        }).format(date);
      }
    }
  }

  return s;
}
